import { useMutation } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { BottomButtonsLayout, Button } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { UPDATE_VEHICLE } from 'graphql/mutations/updateVehicle';
import { GET_CUSTOMER } from 'graphql/queries/getCustomer';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkingStore } from 'stores/parkingStore';
import { useVehicleStore } from 'stores/vehicleStore';
import styled from 'styled-components/native';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationUpdateVehicleArgs,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import CameraRecognitionTutorial from './CameraRecognitionTutorial';
import { useUiStore } from 'stores/uiStore';

const ContentPadding = styled.View`
  padding: 10px 20px;
`;

const VehicleCameraRecognition = () => {
  const { t } = useTranslation();
  const { goBack } = useNavigation<RootNavProps>();
  const { setVehicleData } = useVehicleStore((state) => state);
  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);
  const { params: { saveOnModalConfirm } = { saveOnModalConfirm: false } } =
    useRoute<
      RouteProp<RootStackParamList, 'vehicleCameraRecognitionParking'>
    >();

  const [updateVehicle, { loading }] = useMutation<
    Mutation,
    MutationUpdateVehicleArgs
  >(UPDATE_VEHICLE, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        errorPolicy: 'all',
      },
    ],
    onCompleted: () => {
      showToast('success.updateVehicle', 'success');
      useUiStore.getState().setSheetModal('');
    },
    onError: () => showToast('error.updateVehicle', 'error'),
  });

  const { name, licensePlate, cameraRecognition, appSettings, id } =
    useSelectedParkingVehicle(false, undefined, selectedParkingVehiclePlate) ??
    {};
  const { customer } = useContext(AccountContext);

  const countryCodes = () => {
    const vehicleCountryCodes = cameraRecognition?.countryCodes;
    if (Array.isArray(vehicleCountryCodes) && vehicleCountryCodes.length > 0) {
      return vehicleCountryCodes;
    }
    if (customer?.countryCode) {
      return [customer.countryCode];
    }
    return [];
  };

  const callbackAction = async (updatedAllowedValue: boolean) => {
    if (updatedAllowedValue && licensePlate?.plateText && id) {
      await updateVehicle({
        variables: {
          input: {
            id: id,
            licensePlate: {
              plateText: licensePlate?.plateText,
              countryCode: licensePlate?.countryCode,
            },
            name,
            cameraRecognition: {
              allowed: updatedAllowedValue,
              enabled: updatedAllowedValue,
              countryCodes: countryCodes(),
            },
            appSettings: {
              icon: appSettings?.icon,
              color: appSettings?.color,
              type: appSettings?.type,
            },
          },
        },
      });
    }
    goBack();
  };

  const handleSave = () => {
    if (saveOnModalConfirm) {
      callbackAction(true);
    } else {
      setVehicleData({
        cameraRecognition: {
          enabled: true,
          allowed: true,
          countryCodes: countryCodes(),
        },
      });
      goBack();
    }
  };

  return (
    <BottomButtonsLayout
      withShadow
      contentBottom={
        <ContentPadding>
          <StyledWrapper>
            <Button
              size="l"
              onPress={handleSave}
              text={t('vehicle.cameraRecognition.allow')}
              disabled={loading}
              loading={loading}
            />
          </StyledWrapper>
          <StyledWrapper mb={0}>
            <Button
              size="l"
              buttonType="tertiary"
              text={t('button.cancel')}
              onPress={() => goBack()}
            />
          </StyledWrapper>
        </ContentPadding>
      }>
      <ContentPadding>
        <ColumnWithMargins mt={isWeb ? 100 : 0} />
        <CameraRecognitionTutorial />
      </ContentPadding>
    </BottomButtonsLayout>
  );
};

export default VehicleCameraRecognition;
